<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Expense',
  },
})

const { useAbility } = await import('@casl/vue')
const ability = useAbility()

const { useApi } = await import('@/utils/api')
const router = useRouter()

// ── State ────────────────────────────────────────────────────────────────────
const expenses = ref([])
const categories = ref([])
const loading = ref(false)
const totalItems = ref(0)

// Filters
const search = ref('')
const filterCategory = ref(null)
const filterDateFrom = ref('')
const filterDateTo = ref('')

// Pagination
const page = ref(1)
const perPage = ref(15)

// Delete
const deleteDialog = ref(false)
const deletingId = ref(null)
const deleting = ref(false)

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatRupiah = (val) => {
  if (!val && val !== 0) return '-'
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(val)
}

const formatDate = (val) => {
  if (!val) return '-'
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(val))
}

// ── Fetch ────────────────────────────────────────────────────────────────────
const fetchExpenses = async () => {
  loading.value = true
  try {
    const query = {
      page: page.value,
      per_page: perPage.value,
    }
    if (search.value) query.search = search.value
    if (filterCategory.value) query.category_id = filterCategory.value
    if (filterDateFrom.value) query.date_from = filterDateFrom.value
    if (filterDateTo.value) query.date_to = filterDateTo.value

    const response = await useApi('/api/v1/expenses', { query }).get()
    
    const expensesData = response?.data ?? response
    
    if (Array.isArray(expensesData)) {
      expenses.value = expensesData
    } else {
      expenses.value = expensesData?.data ?? []
    }
    
    // Ambil total dari meta
    totalItems.value = response?.meta?.total ?? expensesData?.meta?.total ?? expenses.value.length
  } catch (e) {
    console.error('Fetch expenses error:', e)
  } finally {
    loading.value = false
  }
}

const fetchCategories = async () => {
  try {
    const response = await useApi('/api/v1/expense-categories', {
      query: { active_only: 1 },
    }).get()
    
    const categoriesData = response?.data ?? response
    
    if (Array.isArray(categoriesData)) {
      categories.value = categoriesData
    } else {
      categories.value = categoriesData?.data ?? []
    }
  } catch (e) {
    console.error('Fetch categories error:', e)
  }
}

onMounted(() => {
  fetchExpenses()
  fetchCategories()
})

// Watch filters — reset page dan refetch
watch([search, filterCategory, filterDateFrom, filterDateTo], () => {
  page.value = 1
  fetchExpenses()
})

watch(page, fetchExpenses)

// ── Delete ───────────────────────────────────────────────────────────────────
const confirmDelete = (id) => {
  deletingId.value = id
  deleteDialog.value = true
}

const doDelete = async () => {
  deleting.value = true
  try {
    await useApi(`/api/v1/expenses/${deletingId.value}`).delete()
    deleteDialog.value = false
    fetchExpenses()
  } catch (e) {
    console.error(e)
  } finally {
    deleting.value = false
  }
}

// ── Clear filters ─────────────────────────────────────────────────────────────
const clearFilters = () => {
  search.value = ''
  filterCategory.value = null
  filterDateFrom.value = ''
  filterDateTo.value = ''
}

const hasActiveFilters = computed(() =>
  search.value || filterCategory.value || filterDateFrom.value || filterDateTo.value,
)

// ── Table headers ─────────────────────────────────────────────────────────────
const headers = [
  { title: 'Tanggal', key: 'expense_date', width: '130px' },
  { title: 'Deskripsi', key: 'description' },
  { title: 'Kategori', key: 'category' },
  { title: 'Jumlah', key: 'amount', align: 'end' },
  { title: 'Berulang', key: 'is_recurring', align: 'center', width: '100px' },
  { title: 'Struk', key: 'receipt_image_url', align: 'center', width: '80px' },
  { title: 'Aksi', key: 'actions', align: 'center', sortable: false, width: '100px' },
]
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">Pengeluaran</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Catat dan pantau seluruh pengeluaran operasional
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Expense')"
            prepend-icon="ri-add-line"
            :to="{ name: 'expense-create' }"
          >
            Tambah Pengeluaran
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Filters -->
    <VCard class="mb-4">
      <VCardText>
        <VRow>
          <VCol cols="12" sm="6" md="4">
            <VTextField
              v-model="search"
              placeholder="Cari deskripsi..."
              prepend-inner-icon="ri-search-line"
              clearable
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" sm="6" md="3">
            <VSelect
              v-model="filterCategory"
              :items="categories"
              item-title="name"
              item-value="id"
              placeholder="Semua Kategori"
              clearable
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="6" md="2">
            <VTextField
              v-model="filterDateFrom"
              type="date"
              label="Dari Tanggal"
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="6" md="2">
            <VTextField
              v-model="filterDateTo"
              type="date"
              label="Sampai Tanggal"
              hide-details
              density="compact"
            />
          </VCol>
          <VCol cols="12" md="1" class="d-flex align-end">
            <VBtn
              v-if="hasActiveFilters"
              variant="tonal"
              color="secondary"
              size="small"
              icon="ri-filter-off-line"
              @click="clearFilters"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Table -->
    <VCard>
      <VDataTable
        :headers="headers"
        :items="expenses"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-footer
      >
        <!-- Tanggal -->
        <template #item.expense_date="{ item }">
          <span class="text-body-2">{{ formatDate(item.expense_date) }}</span>
        </template>

        <!-- Kategori -->
        <template #item.category="{ item }">
          <div class="d-flex align-center gap-2">
            <div
              v-if="item.category?.color"
              :style="`background:${item.category.color};width:10px;height:10px;border-radius:50%;flex-shrink:0;`"
            />
            <span>{{ item.category?.name ?? '-' }}</span>
          </div>
        </template>

        <!-- Jumlah -->
        <template #item.amount="{ item }">
          <span class="font-weight-semibold text-error">
            {{ formatRupiah(item.amount) }}
          </span>
        </template>

        <!-- Berulang -->
        <template #item.is_recurring="{ item }">
          <VIcon
            :icon="item.is_recurring ? 'ri-repeat-line' : 'ri-subtract-line'"
            :color="item.is_recurring ? 'primary' : 'secondary'"
            size="18"
          />
        </template>

        <!-- Struk -->
        <template #item.receipt_image_url="{ item }">
          <VBtn
            v-if="item.receipt_image_url"
            :href="item.receipt_image_url"
            target="_blank"
            icon="ri-image-line"
            variant="text"
            size="small"
            color="primary"
          />
          <span v-else class="text-medium-emphasis">—</span>
        </template>

        <!-- Aksi -->
        <template #item.actions="{ item }">
          <VBtn
            v-if="ability.can('update', 'Expense')"
            icon="ri-pencil-line"
            variant="text"
            size="small"
            color="primary"
            :to="{ name: 'expense-id-edit', params: { id: item.id } }"
          />
          <VBtn
            v-if="ability.can('delete', 'Expense')"
            icon="ri-delete-bin-line"
            variant="text"
            size="small"
            color="error"
            @click="confirmDelete(item.id)"
          />
        </template>

        <!-- Empty state -->
        <template #no-data>
          <div class="text-center py-10">
            <VIcon icon="ri-wallet-line" size="64" color="secondary" class="mb-4" />
            <p class="text-h6 text-medium-emphasis">Belum ada pengeluaran</p>
            <p class="text-body-2 text-medium-emphasis">
              {{
                hasActiveFilters
                  ? 'Coba ubah filter pencarian'
                  : 'Klik "Tambah Pengeluaran" untuk mulai mencatat'
              }}
            </p>
          </div>
        </template>
      </VDataTable>

      <!-- Pagination -->
      <VDivider />
      <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3 pa-4">
        <span class="text-body-2 text-medium-emphasis">
          Total: {{ totalItems }} pengeluaran
        </span>
        <VPagination
          v-if="totalItems > perPage"
          v-model="page"
          :length="Math.ceil(totalItems / perPage)"
          :total-visible="5"
          density="compact"
        />
      </VCardText>
    </VCard>

    <!-- Delete Confirm Dialog -->
    <VDialog v-model="deleteDialog" max-width="400">
      <VCard>
        <VCardTitle class="pa-6 pb-2">Hapus Pengeluaran?</VCardTitle>
        <VCardText class="pa-6 pt-0">
          Data pengeluaran akan dihapus permanen. Struk/foto yang terlampir juga akan ikut terhapus.
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="deleting" @click="deleteDialog = false">
            Batal
          </VBtn>
          <VBtn color="error" :loading="deleting" @click="doDelete">
            Hapus
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
